import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Form } from 'react-bootstrap';
import { AuditRunListItem, AuditRunStatus } from '@blisspointmedia/bpm-types/dist/GoogleAdsAudit';
import { Dropdown } from '@Components/Dropdown';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { useSetError } from '@src/redux/modals';
import cn from 'classnames';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { Size } from '@Components/designConstants';
import { getCompanyAccounts, getCompanyMccs, listAuditRuns } from '../google-ads-audit-api';

const RUN_STATUS_LABELS: Record<AuditRunStatus, string> = {
  RUNNING: 'Running',
  COMPLETE: 'Complete',
  FAILED: 'Failed'
};

const formatRunTime = (iso: string): string => format(new Date(iso), 'MMM d, h:mm a');

// Latest run per account (listAuditRuns returns newest-first), capped so a company
// with a long history still scans quickly.
const latestRunsByAccount = (runs: AuditRunListItem[]): AuditRunListItem[] => {
  const byAccount = new Map<string, AuditRunListItem>();
  runs.forEach(run => {
    if (!byAccount.has(run.accountId)) {
      byAccount.set(run.accountId, run);
    }
  });
  return [...byAccount.values()].slice(0, 8);
};

interface RunRowProps {
  run: AuditRunListItem;
  onOpen: (accountId: string, accountName: string) => void;
}

const RunRow = ({ run, onOpen }: RunRowProps) => (
  <InteractiveDiv
    className='google-ads-audit-account-row'
    onClick={() => onOpen(run.accountId, run.accountName ?? run.accountId)}
  >
    <span className='google-ads-audit-account-name'>{run.accountName ?? run.accountId}</span>
    <span className='google-ads-audit-account-id'>
      {run.status === 'RUNNING' && run.currentActivity
        ? run.currentActivity
        : formatRunTime(run.completedAt ?? run.startedAt)}
    </span>
    <span className={cn('google-ads-audit-run-status', run.status.toLowerCase())}>
      {RUN_STATUS_LABELS[run.status]}
    </span>
  </InteractiveDiv>
);

interface RecentRunsProps {
  company: string;
  onOpen: (accountId: string, accountName: string) => void;
}

const RecentRuns = ({ company, onOpen }: RecentRunsProps) => {
  const setError = useSetError();

  const { data: runs = [], error } = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['google-ads-audit-runs', company],
    queryFn: () => listAuditRuns(company),
    // Keep polling while anything is in flight so an underway run is visibly live
    // here, not just inside the workspace.
    refetchInterval: query => (query.state.data?.some(r => r.status === 'RUNNING') ? 5000 : false)
  });

  useEffect(() => {
    if (error) {
      setError({ title: "Couldn't load recent runs", message: error.message, reportError: error });
    }
  }, [error, setError]);

  const latest = useMemo(() => latestRunsByAccount(runs), [runs]);
  if (latest.length === 0) return undefined;

  return (
    <div>
      <div className='google-ads-audit-list-title'>Latest runs</div>
      <div className='google-ads-audit-account-list'>
        {latest.map(run => (
          <RunRow key={run.id} run={run} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
};

interface ConnectedAccountsProps {
  company: string;
  onOpen: (accountId: string, accountName: string) => void;
}

const ConnectedAccounts = ({ company, onOpen }: ConnectedAccountsProps) => {
  const setError = useSetError();

  const {
    data: accounts = [],
    isFetching,
    error
  } = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['google-ads-audit-company-accounts', company],
    queryFn: () => getCompanyAccounts(company)
  });

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load connected accounts",
        message: error.message,
        reportError: error
      });
    }
  }, [error, setError]);

  if (isFetching) return <div className='google-ads-audit-subtitle'>Loading accounts…</div>;
  if (accounts.length === 0) return undefined;

  return (
    <div>
      <div className='google-ads-audit-list-title'>Connected accounts</div>
      <div className='google-ads-audit-account-list'>
        {accounts.map(account => (
          <InteractiveDiv
            key={account.accountId}
            className='google-ads-audit-account-row'
            onClick={() => onOpen(account.accountId, account.accountName ?? account.accountId)}
          >
            <span className='google-ads-audit-account-name'>
              {account.accountName ?? account.accountId}
            </span>
            <span className='google-ads-audit-account-id'>{account.accountId}</span>
          </InteractiveDiv>
        ))}
      </div>
    </div>
  );
};

interface CompanyMccListProps {
  company: string;
  onSelectMcc: (mccId: string, company: string) => void;
}

const CompanyMccList = ({ company, onSelectMcc }: CompanyMccListProps) => {
  const setError = useSetError();

  const {
    data: mccs = [],
    isFetching,
    error
  } = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['google-ads-audit-company-mccs', company],
    queryFn: () => getCompanyMccs(company)
  });

  useEffect(() => {
    if (error) {
      setError({ title: "Couldn't load MCCs", message: error.message, reportError: error });
    }
  }, [error, setError]);

  if (isFetching) return <div className='google-ads-audit-subtitle'>Loading MCCs…</div>;
  if (mccs.length === 0) {
    return (
      <div className='google-ads-audit-subtitle'>
        No Google Ads MCCs found for this company. Try entering an MCC ID manually below.
      </div>
    );
  }

  return (
    <div>
      <div className='google-ads-audit-list-title'>Browse all accounts by MCC</div>
      <div className='google-ads-audit-account-list'>
        {mccs.map(mcc => (
          <InteractiveDiv
            key={mcc.mccId}
            className='google-ads-audit-account-row'
            onClick={() => onSelectMcc(mcc.mccId, company)}
          >
            <span className='google-ads-audit-account-name'>MCC {mcc.mccId}</span>
            <span className='google-ads-audit-account-id'>
              {conditionallyPluralizeWithCount(mcc.accountCount, 'account')}
            </span>
          </InteractiveDiv>
        ))}
      </div>
    </div>
  );
};

interface CompanyMccPickerProps {
  companyOptions: string[];
  onSelectMcc: (mccId: string, company: string) => void;
  onSelectAccount: (accountId: string, accountName: string, company: string) => void;
  onManualEntry: () => void;
}

export const CompanyMccPicker = ({
  companyOptions,
  onSelectMcc,
  onSelectAccount,
  onManualEntry
}: CompanyMccPickerProps) => {
  const [company, setCompany] = useState<string | null>(null);

  return (
    <div className='google-ads-audit-search-panel'>
      <div className='google-ads-audit-section-title'>Find an account</div>
      <Form.Group>
        <Form.Label>Company</Form.Label>
        <Dropdown
          size={Size.LG}
          searchable
          options={companyOptions}
          value={company}
          onChange={setCompany}
          placeholder='Select a company...'
          menuPortalTarget={document.body}
        />
      </Form.Group>
      {company && (
        <>
          <RecentRuns company={company} onOpen={(id, name) => onSelectAccount(id, name, company)} />
          <ConnectedAccounts
            company={company}
            onOpen={(id, name) => onSelectAccount(id, name, company)}
          />
          <CompanyMccList company={company} onSelectMcc={onSelectMcc} />
        </>
      )}
      <InteractiveDiv className='google-ads-audit-back-link' onClick={onManualEntry}>
        Enter an MCC ID manually instead
      </InteractiveDiv>
    </div>
  );
};
